import * as Sentry from "@sentry/react";
import React from "react";
import ReactDOM from "react-dom/client";
import { AuthProvider } from "react-oidc-context";

import { App } from "./App";
import { oidcConfig } from "./auth/oidc-config";
import { installSessionExpiryListeners } from "./auth/session-expiry";
import { installCspNonce } from "./lib/csp-nonce";
import { scrubEvent } from "./lib/sentry-scrub";
// Self-hosted fonts (CHP-1288) — these register the "Open Sans Variable" /
// "Sora Variable" families that index.css names in --font-sans/--font-heading,
// so they must be imported BEFORE it. Vite emits the .woff2 files into
// /assets/, keeping the app free of any cross-origin subresource (which is
// what lets COEP: require-corp hold — CHP-1289).
import "@fontsource-variable/open-sans";
import "@fontsource-variable/open-sans/wght-italic.css";
import "@fontsource-variable/sora";
import "./index.css";

// Demo-scoped utility classes (.wordmark, .chip, .display, .mono-xs,
// .pill, .dot) live in demo/theme.css and are gated by the `.demo-theme`
// class on their nearest ancestor. The login marketing panel opts in
// via its parent <aside>; the rest of the app stays un-skinned because
// no element carries the class globally.
void import("./demo/theme.css");

// CHP-1337: publish the edge-minted CSP nonce and drop the tag that carried it.
// Ordering is load-bearing twice over, and tests/lib/csp-nonce.test.ts pins both:
//   * BEFORE createRoot — react-style-singleton reads the nonce from get-nonce's
//     module global at style-tag-creation time, so anything injected before the
//     nonce is published is unnonced and style-src-elem blocks it.
//   * BEFORE Sentry.init — Replay's DOM serialiser records element attributes
//     verbatim and bypasses beforeSend, so the tag must be gone before rrweb
//     takes its first snapshot or the live nonce is uploaded off-box.
installCspNonce();

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    integrations: [
      Sentry.browserTracingIntegration(),
      // Replay is error-only and fully masked: partner names, deal values and
      // free-text notes must never leave the browser in a replay payload.
      Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    beforeSend: (event) => scrubEvent(event),
  });
}

// AuthProvider sits OUTSIDE React.StrictMode by design. StrictMode
// double-invokes effects in development, which causes
// react-oidc-context's UserManager to consume the OIDC `state`
// parameter twice — the second call fails with "No matching state
// found in storage" and the user has to reload to land on a working
// session. Hoisting the provider above StrictMode keeps the rest of
// the tree benefiting from StrictMode's checks while letting the
// OIDC callback fire exactly once.
// CHP-419: register the proactive session-expiry listeners once, here (outside
// StrictMode) so they attach exactly once rather than double-registering on a
// StrictMode effect re-run.
installSessionExpiryListeners();

ReactDOM.createRoot(document.getElementById("root")!).render(
  <AuthProvider {...oidcConfig}>
    <React.StrictMode>
      <App />
    </React.StrictMode>
  </AuthProvider>,
);
